import loadable from '@loadable/component';
import { MAIN_DIV_ID } from 'src/constants/constants';
import { Skeleton } from 'src/components/loading-skeleton';
import { SkeletonType } from 'src/components/loading-skeleton/dto';
import LanguageFunction from '@/utils/languageCode';
import { PageBannerType, PageType } from '@/types/page-type';
import { Loading } from '@/components/loading';

const ArticleTemplate = loadable(() => import('@/templates/article-template'), {
    fallback: <Loading />,
});

const ContactPageTemplate = loadable(() => import('@/templates/contact-page-template'), {
    fallback: <Skeleton variant={SkeletonType.FullPageSpinner} />,
});

const LandingTemplate = loadable(() => import('@/templates/landing-template'), {
    fallback: <Skeleton variant={SkeletonType.FullPageSpinner} />,
});

const CalendarPageTemplate = loadable(() => import('@/templates/calendar-page-template'), {
    fallback: <Loading />,
});

const QualtricsPageTemplate = loadable(() => import('@/templates/qualtrics-page-template'), {
    fallback: <Loading />,
});

const AccreditationPageTemplate = loadable(() => import('@/templates/accreditation-page-template'), {
    fallback: <Loading />,
});

const LogInHoldingScreenTemplate = loadable(() => import('@/templates/log-in-holding-screen-template'), {
    fallback: <Loading />,
});

const MediaPageTemplate = loadable(() => import('@/templates/media-page-template'), {
    fallback: <Loading />,
});

const VideoPageTemplate = loadable(() => import('@/templates/video-page-template'), {
    fallback: <Loading />,
});

const TournamentPageTemplate = loadable(() => import('@/templates/tournament-page-template'), {
    fallback: <Loading />,
});

const NewsroomTemplate = loadable(() => import('@/templates/newsroom-template'), {
    fallback: <Loading />,
});

const SearchPageTemplate = loadable(() => import('@/templates/search-page-template'), {
    fallback: <Loading />,
});

const ProfilePageTemplate = loadable(() => import('@/templates/profile-page-template'), {
    fallback: <Loading />,
});

const BusSchedulePageTemplate = loadable(() => import('@/templates/bus-schedule-page-template'), {
    fallback: <Loading />,
});

const ImageGalleryPageTemplate = loadable(() => import('@/templates/image-gallery-page-template'), {
    fallback: <Loading />,
});

const BusSchedulePageTemplateNoFallback = loadable(() => import('@/templates/bus-schedule-page-template'), {});

const HighlightedContentTemplate = loadable(() => import('@/templates/highlighted-content-page-template'), {});

const HeadlessCalendarTemplate = loadable(() => import('@/templates/headless-calendar-page-template'), {});

const HeadlessSocialMediaPageTemplate = loadable(() => import('@/templates/headless-social-media-page-template'), {});

const RegistrationFormTemplate = loadable(() => import('@/templates/registration-form-template'), {});

const HostCityTemplate = loadable(() => import('@/templates/host-city-template'), {
    fallback: <Skeleton variant={SkeletonType.FullPageSpinner} />,
});

export enum TEMPLATE {
    ARTICLE = 'articlePageTemplate',
    LANDING = 'landingTemplate',
    GENERAL_INFORMATION = 'generalInformationTemplate',
    CONTACT_PAGE_TEMPLATE = 'contactPageTemplate',
    CALENDAR = 'calendarTemplate',
    QUALTRICS = 'qualtricsTemplate',
    LOG_IN_HOLDING_SCREEN = 'logInHoldingScreenTemplate',
    MEDIA_PAGE = 'mediaPageTemplate',
    VIDEO_PAGE = 'videoPageTemplate',
    TOURNAMENT_TEMPLATE = 'tournamentPageTemplate',
    ACCREDITATION_TEMPLATE = 'accreditationTemplate',
    NEWSROOM = 'newsroomTemplate',
    SEARCH_TEMPLATE = 'searchPageTemplate',
    BUS_SCHEDULE_TEMPLATE = 'busScheduleTemplate',
    HIGHLIGHTED_CONTENT_TEMPLATE = 'highlightedContentTemplate',
    IMAGE_GALLERY_PAGE_TEMPLATE = 'imageGalleryPageTemplate',
    HEADLESS_CALENDAR_TEMPLATE = 'headlessCalendarTemplate',
    HEADLESS_SOCIAL_MEDIA_TEMPLATE = 'headlessSocialMediaTemplate',
    PROFILE_PAGE_TEMPLATE = 'profilePageTemplate',
    REGISTRATION_FORM_TEMPLATE = 'registrationFormTemplate',
    HOST_CITY_TEMPLATE = 'hostCityTemplate',
}

const TemplateRenderer = ({
    page,
    isSubpage,
    liveGamesBanner,
}: {
    page: PageType;
    isSubpage?: boolean;
    liveGamesBanner?: PageBannerType;
}) => {
    if (!page) return <></>;

    const render = () => {
        if (!page) LanguageFunction.removeArticleLocalStorageKey();
        switch (page.template as TEMPLATE) {
            case TEMPLATE.ARTICLE:
                LanguageFunction.storeArticleEntryId(page.entryId);
                return <ArticleTemplate page={page} />;
            case TEMPLATE.TOURNAMENT_TEMPLATE:
                return <TournamentPageTemplate page={page} liveGamesBanner={liveGamesBanner} />;
            case TEMPLATE.GENERAL_INFORMATION:
                return <ArticleTemplate page={page} />;
            case TEMPLATE.CONTACT_PAGE_TEMPLATE:
                return <ContactPageTemplate page={page} />;
            case TEMPLATE.LANDING:
                return <LandingTemplate page={page} isSubpage={isSubpage} liveGameBanner={liveGamesBanner} />;
            case TEMPLATE.CALENDAR:
                return <CalendarPageTemplate page={page} />;
            case TEMPLATE.QUALTRICS:
                return <QualtricsPageTemplate page={page} />;
            case TEMPLATE.MEDIA_PAGE:
                return <MediaPageTemplate page={page} />;
            case TEMPLATE.VIDEO_PAGE:
                return <VideoPageTemplate page={page} />;
            case TEMPLATE.LOG_IN_HOLDING_SCREEN:
                return <LogInHoldingScreenTemplate page={page} />;
            case TEMPLATE.ACCREDITATION_TEMPLATE:
                return <AccreditationPageTemplate page={page} />;
            case TEMPLATE.NEWSROOM:
                return <NewsroomTemplate page={page} />;
            case TEMPLATE.SEARCH_TEMPLATE:
                return <SearchPageTemplate page={page} />;
            case TEMPLATE.PROFILE_PAGE_TEMPLATE:
                return <ProfilePageTemplate page={page} />;
            case TEMPLATE.BUS_SCHEDULE_TEMPLATE:
                return page.headless ? (
                    <BusSchedulePageTemplateNoFallback page={page} />
                ) : (
                    <BusSchedulePageTemplate page={page} />
                );
            case TEMPLATE.HIGHLIGHTED_CONTENT_TEMPLATE:
                return <HighlightedContentTemplate page={page} />;
            case TEMPLATE.IMAGE_GALLERY_PAGE_TEMPLATE:
                return <ImageGalleryPageTemplate page={page} />;
            case TEMPLATE.HEADLESS_CALENDAR_TEMPLATE:
                return <HeadlessCalendarTemplate page={page} />;
            case TEMPLATE.HEADLESS_SOCIAL_MEDIA_TEMPLATE:
                return <HeadlessSocialMediaPageTemplate page={page} />;
            case TEMPLATE.REGISTRATION_FORM_TEMPLATE:
                return <RegistrationFormTemplate page={page} />;
            case TEMPLATE.HOST_CITY_TEMPLATE:
                return <HostCityTemplate page={page} />;
            default:
                return null;
        }
    };

    return (
        <div id={MAIN_DIV_ID} dir={page.language === 'ar' ? 'rtl' : 'ltr'}>
            {render()}
        </div>
    );
};

export default TemplateRenderer;
